import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { errMsg, fmtPeriod, useApi } from "@/lib/api";

const STATUS = {
  complimentary: "Complimentary access", active: "Active", trialing: "Free trial", past_due: "Payment overdue",
  canceled: "Cancelled", not_subscribed: "Not subscribed", incomplete: "Payment incomplete", unpaid: "Unpaid",
};
const usd = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const Plan = ({ label, price, per, onPick, busy, testid, disabled }) => (
  <div className="panel p-6 flex flex-col">
    <div className="small-label">{label}</div>
    <div className="num text-3xl font-semibold mt-3">{price ? usd(price) : "—"}<span className="text-sm text-slate-500 font-normal"> / {per}</span></div>
    <Button onClick={onPick} disabled={busy || disabled || !price} className="mt-6 rounded-sm" data-testid={testid}>
      {busy ? "Redirecting…" : "Subscribe"}
    </Button>
  </div>
);

export default function Billing() {
  const api = useApi();
  const { me, loadMe } = useApp();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(null);
  const isAdmin = me?.role === "admin";
  const load = () => api.get("/billing").then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (interval) => {
    setBusy(interval);
    try {
      const r = await api.post("/billing/checkout", { interval, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };

  const toggleCancel = async (cancel) => {
    setBusy("cancel");
    try {
      await api.post(cancel ? "/billing/cancel" : "/billing/resume");
      toast.success(cancel ? "Subscription will end at the close of this period" : "Subscription resumed");
      await load();
      await loadMe();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(null);
  };

  if (!b) return <p className="small-label">Loading…</p>;
  const key = b.complimentary ? "complimentary" : b.status || "not_subscribed";
  const hasSub = ["active", "trialing", "past_due"].includes(b.status);
  return (
    <div className="max-w-4xl">
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Billing</h1>
      <p className="text-slate-500 mt-3">Subscription for <b className="text-slate-800">{b.firm}</b>.</p>
      <div className="panel p-6 mt-10 flex flex-wrap items-center gap-4" data-testid="billing-status">
        {b.can_edit ? <CheckCircle2 className="h-6 w-6 text-emerald-700" /> : <Lock className="h-6 w-6 text-amber-700" />}
        <div>
          <div className="font-serif text-2xl" data-testid="billing-status-label">{STATUS[key] || key}</div>
          <div className="text-sm text-slate-500 mt-1">
            {b.complimentary ? "Full access is provided at no charge." : b.can_edit ? "Full access: uploads and edits are on." : "View-only: statements and PDFs work, uploads and edits are paused."}
            {hasSub && b.current_period_end && ` ${b.cancel_at_period_end ? "Ends" : "Renews"} ${fmtPeriod(b.current_period_end.slice(0, 10))}.`}
            {hasSub && b.amount ? ` ${usd(b.amount)} per ${b.interval}.` : ""}
          </div>
        </div>
        {isAdmin && hasSub && !b.complimentary && (
          <Button variant="outline" className="ml-auto rounded-sm" disabled={busy === "cancel"} onClick={() => toggleCancel(!b.cancel_at_period_end)} data-testid="billing-cancel-toggle">
            {b.cancel_at_period_end ? "Resume subscription" : "Cancel at period end"}
          </Button>
        )}
      </div>
      {!b.complimentary && !hasSub && (
        <>
          <div className="small-label mt-10">Choose a plan {b.trial_days > 0 && `· ${b.trial_days}-day free trial`}</div>
          {!b.prices.month && !b.prices.year && <p className="text-sm text-slate-500 mt-3" data-testid="billing-no-prices">Pricing hasn't been published yet. Please check back soon.</p>}
          {!isAdmin && <p className="text-sm text-slate-500 mt-3">Only a firm admin can subscribe.</p>}
          <div className="grid sm:grid-cols-2 gap-6 mt-4">
            <Plan label="Monthly" price={b.prices.month} per="month" busy={busy === "month"} disabled={!isAdmin} onPick={() => subscribe("month")} testid="billing-subscribe-month" />
            <Plan label="Yearly" price={b.prices.year} per="year" busy={busy === "year"} disabled={!isAdmin} onPick={() => subscribe("year")} testid="billing-subscribe-year" />
          </div>
        </>
      )}
    </div>
  );
}
